import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import HeroVisual from "@/components/HeroVisual";

const EASE = [0.22, 1, 0.36, 1];

// Two-part straplines: function line, then the positive outcome.
// The first pair carries the current positioning statement.
const PAIRS = [
    ["One Intelligent Platform.", "Every Part of Your Business."],
    ["Smarter Operations.", "Real Results."],
    ["Jobs Managed End-to-End.", "Nothing Gets Missed."],
    ["Teams Kept in Sync.", "Every Job Flows."],
    ["Less Admin. More Done.", "A More Profitable Business."],
];

// Types line 1, then line 2, holds, deletes both, moves to the next pair.
export function usePairTypewriter(pairs) {
    const [text, setText] = useState(["", ""]);
    useEffect(() => {
        let pair = 0;
        let char = 0;
        let phase = "l1"; // l1 -> l2 -> hold -> d2 -> d1 -> next pair
        let timer;
        const tick = () => {
            const [l1, l2] = pairs[pair];
            if (phase === "l1") {
                char += 1;
                setText([l1.slice(0, char), ""]);
                if (char === l1.length) {
                    phase = "l2";
                    char = 0;
                    timer = setTimeout(tick, 200);
                    return;
                }
                timer = setTimeout(tick, 55);
            } else if (phase === "l2") {
                char += 1;
                setText([l1, l2.slice(0, char)]);
                if (char === l2.length) {
                    phase = "hold";
                    timer = setTimeout(tick, 2400);
                    return;
                }
                timer = setTimeout(tick, 55);
            } else if (phase === "hold") {
                phase = "d2";
                char = l2.length;
                timer = setTimeout(tick, 30);
            } else if (phase === "d2") {
                char -= 1;
                setText([l1, l2.slice(0, char)]);
                if (char === 0) {
                    phase = "d1";
                    char = l1.length;
                    timer = setTimeout(tick, 130);
                    return;
                }
                timer = setTimeout(tick, 24);
            } else {
                char -= 1;
                setText([l1.slice(0, char), ""]);
                if (char === 0) {
                    phase = "l1";
                    pair = (pair + 1) % pairs.length;
                    char = 0;
                    timer = setTimeout(tick, 380);
                    return;
                }
                timer = setTimeout(tick, 24);
            }
        };
        timer = setTimeout(tick, 1200);
        return () => clearTimeout(timer);
    }, [pairs]);
    return text;
}

export const Caret = () => (
    <span
        aria-hidden="true"
        className="ml-1.5 inline-block h-[0.8em] w-[4px] translate-y-[0.06em] animate-pulse rounded-full bg-gradient-to-b from-[#00E5FF] to-[#A855F7]"
        data-testid="hero-caret"
    />
);

const Hero = () => {
    const [line1, line2] = usePairTypewriter(PAIRS);

    const scrollToWorkflow = (e) => {
        e.preventDefault();
        document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <main
            className="relative z-10 flex flex-1 items-center px-6 sm:px-12"
            data-testid="hero-section"
        >
            <div className="grid w-full max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_1fr]">
                <div>
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.8, ease: EASE }}
                    className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] sm:text-xs"
                    data-testid="hero-eyebrow"
                >
                    Operations Software for Service Businesses
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.55, duration: 0.6, ease: EASE }}
                    className="mt-4 font-display font-bold leading-[1.08] tracking-tight text-[clamp(2rem,4.5vw,4.5rem)]"
                    data-testid="hero-headline"
                >
                    <span className="block" style={{ minHeight: "1.08em" }}>
                        <span className="text-white" data-testid="hero-typed-1">
                            {line1}
                        </span>
                        {!line2 && <Caret />}
                    </span>
                    <span className="block" style={{ minHeight: "2.16em" }}>
                        <span className="text-brand-gradient" data-testid="hero-typed-2">
                            {line2}
                        </span>
                        {line2 && <Caret />}
                    </span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
                    className="mt-6 max-w-lg text-sm leading-relaxed text-[#B8C8DF] sm:text-base"
                    data-testid="hero-sub"
                >
                    Connect sales, marketing, customers, operations, field teams, stock and finance
                    in one AI-powered platform designed to help businesses work smarter.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.15, duration: 0.8, ease: EASE }}
                    className="mt-8 flex flex-wrap items-center gap-4 sm:mt-10"
                >
                    <a
                        href="mailto:enquires@tedopsai.com?subject=Book%20a%20TedOps%20AI%20demo"
                        className="group inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] px-7 py-3.5 text-sm font-bold text-white shadow-[0_14px_35px_-10px_rgba(0,102,255,0.6)] transition-transform duration-300 hover:scale-[1.04]"
                        data-testid="hero-book-demo"
                    >
                        Book a demo
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                    <a
                        href="#how-it-works"
                        onClick={scrollToWorkflow}
                        className="inline-flex items-center gap-2.5 rounded-full border border-white/20 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:border-[#00E5FF]/60 hover:text-[#00E5FF]"
                        data-testid="hero-see-how"
                    >
                        <Play className="h-4 w-4" />
                        See how it works
                    </a>
                </motion.div>
                </div>
                <HeroVisual />
            </div>
        </main>
    );
};

export default Hero;
